import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { 
    Send, 
    Building2, 
    MapPin, 
    Briefcase, 
    CheckCircle2, 
    AlertCircle, 
    Edit, 
    FileText, 
    Info, 
    File, 
    X, 
    Lightbulb, 
    ShieldCheck,
    Rocket
} from 'lucide-react';
import { applyToJob } from '../../services/applicationService';
import { getJobSeekerProfile } from '../../services/profileService';

const JobApplicationModal = ({ 
    show, 
    onHide, 
    job, 
    onApplicationSuccess 
}) => {
    const [formData, setFormData] = useState({
        coverLetter: '',
        resumePath: '',
        resumeFile: null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Profile resume states
    const [profileResume, setProfileResume] = useState(null);
    const [useProfileResume, setUseProfileResume] = useState(false);
    const [loadingProfile, setLoadingProfile] = useState(false);

    useEffect(() => {
        if (show) {
            const checkProfileResume = async () => {
                try {
                    setLoadingProfile(true);
                    const response = await getJobSeekerProfile();
                    const profile = (response && response.data && typeof response.data === 'object')
                        ? response.data
                        : response;
                    
                    if (profile && profile.resume) {
                        setProfileResume(profile.resume);
                        setUseProfileResume(true);
                        setFormData(prev => ({
                            ...prev,
                            resumePath: profile.resume,
                            resumeFile: null
                        }));
                    } else {
                        setProfileResume(null);
                        setUseProfileResume(false);
                    }
                } catch (err) {
                    console.error('Failed to load profile resume:', err);
                } finally {
                    setLoadingProfile(false);
                }
            };
            checkProfileResume();
        } else {
            setProfileResume(null);
            setUseProfileResume(false);
        }
    }, [show]);

    const handleResumeOptionChange = (useProfile) => {
        setUseProfileResume(useProfile);
        if (useProfile) {
            setFormData(prev => ({
                ...prev,
                resumePath: profileResume,
                resumeFile: null
            }));
            if (error) setError(null);
        } else {
            setFormData(prev => ({
                ...prev,
                resumePath: '',
                resumeFile: null
            }));
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (error) setError(null);
    };

    // Handle file upload
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (5MB max)
            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
            if (file.size > maxSize) {
                setError('File size must be less than 5MB');
                e.target.value = ''; // Clear the input
                return;
            }

            // Validate file type
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                setError('Please upload a PDF, DOC, or DOCX file');
                e.target.value = ''; // Clear the input
                return;
            }

            setFormData(prev => ({
                ...prev,
                resumeFile: file,
                resumePath: file.name // Set the file name as path for now
            }));
            
            // Clear any previous errors
            if (error) setError(null);
        }
    };

    // Remove selected file
    const removeFile = () => {
        setFormData(prev => ({
            ...prev,
            resumeFile: null,
            resumePath: ''
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        // Prevent multiple submissions
        if (isSubmitting || success) return;
        
        setIsSubmitting(true);
        setError(null);

        try {
            let resumePath = formData.resumePath;
            
            if (formData.resumeFile) {
                // In a production environment, you'd upload to a file storage service
                resumePath = `resumes/${Date.now()}_${formData.resumeFile.name}`;
            }

            // Use the fixed applyToJob method from applicationService
            const response = await applyToJob(job.jobPostId || job.id, {
                coverLetter: formData.coverLetter.trim() || '',
                resumePath: resumePath || null,
                resumeFile: formData.resumeFile
            });

            // Robust check: Success if explicitly true OR if we got back an application object with an ID
            const isSuccess = response && (response.success === true || response.id || response.jobId);
            
            if (isSuccess) {
                setSuccess(true);
                if (onApplicationSuccess) {
                    onApplicationSuccess(job.jobPostId || job.id, response);
                }
                setTimeout(() => {
                    handleClose();
                }, 2000);
            } else {
                throw new Error(response.message || 'Failed to submit application');
            }

        } catch (error) {
            console.error('Application submission error:', error);
            
            const status = error.response?.status || error.status;
            const apiMessage = error.response?.data?.message || error.response?.data?.error;
            
            if (status === 409 || (apiMessage && apiMessage.toLowerCase().includes('already applied')) || (error.message && error.message.includes('409'))) {
                setError('You have already applied for this job.');
                setTimeout(() => {
                    if (onApplicationSuccess) {
                        onApplicationSuccess(job.jobPostId || job.id, { success: true, message: 'Already applied' });
                    }
                    handleClose();
                }, 2000);
            } else if (status === 403 || (apiMessage && apiMessage.includes('Only job seekers'))) {
                setError('Only job seekers can apply for jobs. Please log in as a job seeker.');
            } else if (status === 404) {
                setError('This job is no longer available.');
            } else if (status === 401) {
                setError('Your session has expired. Please log in again.');
            } else {
                setError(apiMessage || (error.response?.data?.error) || error.message || 'Failed to submit application. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (success) {
            setFormData({
                coverLetter: '',
                resumePath: '',
                resumeFile: null
            });
            setSuccess(false);
        }
        setError(null);
        onHide();
    };

    const maxCoverLetterLength = 5000;
    const coverLetterCount = formData.coverLetter.length;

    if (!job) return null;

    return (
        <Modal 
            show={show} 
            onHide={handleClose} 
            size="lg" 
            centered
            backdrop="static"
            className="job-application-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold d-flex align-items-center">
                    <Rocket className="text-primary me-2" size={24} />
                    {success ? 'Application Sent!' : 'Apply for Job'}
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="pt-3">
                {success ? (
                    <div className="text-center py-5 fade-in">
                        <div className="success-animation mb-4">
                            <CheckCircle2 size={80} className="text-success" />
                        </div>
                        <h3 className="fw-bold mb-3">Application Successful!</h3>
                        <p className="text-muted mb-4">
                            Your application for <strong>{job.jobTitle}</strong> at <strong>{job.companyName || 'Company'}</strong> has been sent successfully.
                        </p>
                        <Alert variant="info" className="d-inline-block px-4">
                            <Info className="me-2" size={18} />
                            The recruiter will review your profile and contact you.
                        </Alert>
                    </div>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        {error && (
                            <Alert variant="danger" className="mb-4 d-flex align-items-center">
                                <AlertCircle className="me-3" size={24} />
                                <div>
                                    <h6 className="alert-heading mb-1 fw-bold">Application Failed:</h6>
                                    <p className="mb-0 small">{error}</p>
                                </div>
                            </Alert>
                        )}

                        <div className="job-summary-card mb-4 p-3 bg-light rounded d-flex align-items-center">
                            <div className="company-logo-placeholder me-3 bg-white p-2 rounded">
                                <Building2 className="text-primary" size={32} />
                            </div>
                            <div>
                                <h5 className="mb-1 fw-bold">{job.jobTitle}</h5>
                                <div className="text-muted small d-flex flex-wrap gap-3">
                                    <span className="d-flex align-items-center">
                                        <Building2 size={14} className="me-1" />
                                        {job.companyName || 'Company'}
                                    </span>
                                    <span className="d-flex align-items-center">
                                        <MapPin size={14} className="me-1" />
                                        {job.jobLocation || 'Remote'}
                                    </span>
                                    <span className="d-flex align-items-center">
                                        <Briefcase size={14} className="me-1" />
                                        {job.jobType || 'Full-time'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <Form.Group className="mb-4">
                            <Form.Label className="d-flex align-items-center fw-bold">
                                <Edit className="me-2 text-primary" size={18} />
                                Cover Letter <span className="text-muted ms-1">(Optional)</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={8}
                                name="coverLetter"
                                value={formData.coverLetter}
                                onChange={handleInputChange}
                                placeholder="Write a personalized cover letter to increase your chances of getting hired..."
                                maxLength={maxCoverLetterLength}
                                disabled={isSubmitting}
                                className="cover-letter-textarea"
                            />
                            <Form.Text className="text-muted d-flex justify-content-between">
                                <span>Tip: Mention specific skills and experiences relevant to this role</span>
                                <span className={coverLetterCount > maxCoverLetterLength * 0.9 ? 'text-warning' : ''}>
                                    {coverLetterCount}/{maxCoverLetterLength}
                                </span>
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label className="d-flex align-items-center fw-bold">
                                <FileText className="me-2 text-primary" size={18} />
                                Resume <span className="text-muted ms-1">(Optional)</span>
                            </Form.Label>
                            
                            {loadingProfile ? (
                                <div className="d-flex align-items-center py-2">
                                    <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                                    <span className="text-muted small">Checking profile for saved resume...</span>
                                </div>
                            ) : profileResume ? (
                                <div className="profile-resume-selector border p-3 rounded bg-white shadow-sm mb-3">
                                    <Form.Check
                                        type="radio"
                                        id="use-profile-resume"
                                        name="resumeOption"
                                        label={
                                            <span>
                                                Use resume from profile (<strong>{profileResume.split('/').pop().replace(/^\d+_/, '')}</strong>)
                                            </span>
                                        }
                                        checked={useProfileResume}
                                        onChange={() => handleResumeOptionChange(true)}
                                        disabled={isSubmitting}
                                        className="fw-semibold mb-2"
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="upload-new-resume"
                                        name="resumeOption"
                                        label="Upload a different resume"
                                        checked={!useProfileResume}
                                        onChange={() => handleResumeOptionChange(false)}
                                        disabled={isSubmitting}
                                        className="fw-semibold"
                                    />
                                </div>
                            ) : null}

                            {(!profileResume || !useProfileResume) && !loadingProfile && (
                                <div className="resume-upload-section">
                                    <Form.Control
                                        type="file"
                                        name="resumeFile"
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleFileChange}
                                        disabled={isSubmitting}
                                        className="mb-2"
                                    />
                                    {formData.resumeFile && (
                                        <div className="selected-file mt-2 p-2 bg-light rounded d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <File className="me-2 text-primary" size={16} />
                                                <span className="file-name">{formData.resumeFile.name}</span>
                                            </div>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-danger p-0 ms-2 d-flex align-items-center"
                                                onClick={removeFile}
                                                disabled={isSubmitting}
                                            >
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Form.Group>
                    </Form>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0 pb-4 px-4">
                {!success && (
                    <div className="d-flex justify-content-end w-100 gap-2">
                        <Button 
                            variant="outline-secondary" 
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className="px-4 rounded-pill"
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            type="submit"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="px-4 rounded-pill d-flex align-items-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Send className="me-2" size={18} />
                                    Submit Application
                                </>
                            )}
                        </Button>
                    </div>
                )}
                {success && (
                    <Button variant="primary" onClick={handleClose} className="px-5 rounded-pill">
                        Close
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default JobApplicationModal;